import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, inrShort, fmtD } from "@/lib/api";
import { PageHeader, Sel, VerticalBadge, Kpi } from "@/components/common";

export default function Pipeline() {
  const { meta, isAdmin, salesUsers } = useAuth();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ vertical: "", source: "", assigned_to: sp.get("user") || "" });
  const [leads, setLeads] = useState([]);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const nav = useNavigate();
  const load = useCallback(() => {
    api.get("/leads", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => setLeads(r.data));
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const cols = useMemo(() => meta.stages.map((s) => {
    const items = leads.filter((l) => l.stage === s);
    return { stage: s, items, value: items.reduce((a, l) => a + (l.deal_value || 0), 0) };
  }), [leads, meta.stages]);
  const open = leads.filter((l) => !["Won", "Lost"].includes(l.stage));
  const pv = open.reduce((a, l) => a + (l.deal_value || 0), 0);
  const wp = open.reduce((a, l) => a + (l.expected_revenue || 0), 0);
  const won = leads.filter((l) => l.stage === "Won").reduce((a, l) => a + (l.deal_value || 0), 0);

  const drop = async (stage) => {
    setOver(null);
    if (!drag || drag.stage === stage) return;
    let reason;
    if (stage === "Lost") reason = window.prompt("Reason for losing this deal?") || "";
    setLeads((ls) => ls.map((l) => (l.id === drag.id ? { ...l, stage } : l)));
    try { await api.patch(`/leads/${drag.id}/stage`, { stage, reason }); toast.success(`${drag.company} → ${stage}`); }
    catch (e) { toast.error(errMsg(e)); }
    setDrag(null);
    load();
  };

  return (
    <div>
      <PageHeader title="Sales pipeline" subtitle="Drag deals between stages to update them">
        {isAdmin && <div className="w-44"><Sel testid="pipeline-filter-salesperson" value={f.assigned_to} onChange={set("assigned_to")} placeholder="All salespeople" options={salesUsers.map((u) => ({ value: u.id, label: u.name }))} /></div>}
        <div className="w-36"><Sel testid="pipeline-filter-vertical" value={f.vertical} onChange={set("vertical")} placeholder="All verticals" options={meta.verticals} /></div>
        <div className="w-40"><Sel testid="pipeline-filter-source" value={f.source} onChange={set("source")} placeholder="All sources" options={meta.sources} /></div>
      </PageHeader>
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testid="pipeline-total-value" label="Pipeline value" value={inrShort(pv)} sub={`${open.length} open deals`} />
        <Kpi testid="pipeline-weighted-value" label="Weighted pipeline" value={inrShort(wp)} sub="Value × probability" />
        <Kpi label="Won value" value={inrShort(won)} />
        <Kpi label="Avg. open deal" value={inrShort(open.length ? pv / open.length : 0)} />
      </div>
      <div className="kanban-col flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
        {cols.map((c) => (
          <div key={c.stage} data-testid={`pipeline-column-${c.stage}`}
            onDragOver={(e) => { e.preventDefault(); setOver(c.stage); }} onDragLeave={() => setOver(null)} onDrop={() => drop(c.stage)}
            className={`flex w-64 shrink-0 flex-col rounded-xl border bg-slate-100/70 transition-colors ${over === c.stage ? "border-primary bg-emerald-50" : ""}`}>
            <div className={`rounded-t-xl border-b px-3 py-2.5 ${c.stage === "Won" ? "bg-emerald-50" : c.stage === "Lost" ? "bg-rose-50" : "bg-white"}`}>
              <div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-wide text-slate-700">{c.stage}</p><span className="num rounded-full bg-slate-200 px-2 text-[11px] font-bold">{c.items.length}</span></div>
              <p className="num mt-0.5 text-xs text-muted-foreground">{inrShort(c.value)}</p>
            </div>
            <div className="max-h-[62vh] flex-1 space-y-2 overflow-y-auto p-2 scroll-thin">
              {c.items.map((l) => (
                <div key={l.id} draggable data-testid={`pipeline-card-${l.id}`} onDragStart={() => setDrag(l)} onDragEnd={() => setOver(null)} onClick={() => nav(`/leads/${l.id}`)}
                  className="cursor-grab rounded-lg border bg-white p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing">
                  <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold leading-snug text-slate-900">{l.deal_name || l.company}</p><VerticalBadge v={l.vertical} /></div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{l.company}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="num text-sm font-semibold">{inrShort(l.deal_value)}</span>
                    <span className="num text-[11px] text-muted-foreground">{l.probability}% · {inrShort(l.expected_revenue)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t pt-2 text-[11px] text-muted-foreground">
                    <span className={`flex items-center gap-1 ${l.days_in_stage >= meta.stuck_days && !["Won", "Lost"].includes(l.stage) ? "font-bold text-rose-600" : ""}`}><Clock className="h-3 w-3" />{l.days_in_stage}d in stage</span>
                    <span>{isAdmin ? l.assigned_name?.split(" ")[0] : `Close ${fmtD(l.expected_close).slice(0, 6)}`}</span>
                  </div>
                  {l.next_action && <p className="mt-1 truncate text-[11px] text-slate-600">→ {l.next_action}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
